import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Files, Play, Trash2, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api_endpoints } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    PageHeader,
    Btn,
    Card,
    Eyebrow,
    VerdictBadge,
} from "@/components/app/ui";

export default function Bulk() {
    const { t } = useTranslation();
    const [text, setText] = React.useState("");
    const [collections, setCollections] = React.useState([]);
    const [collectionId, setCollectionId] = React.useState("");
    const [job, setJob] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        api_endpoints
            .collections()
            .then((cs) => {
                setCollections(cs);
                setCollectionId(cs.find((c) => c.isDefault)?.id || cs[0]?.id || "");
            })
            .catch(() => {});
        api_endpoints
            .bulkActive()
            .then((j) => j && j.jobId && setJob(j))
            .catch(() => {});
    }, []);

    // Poll active job
    React.useEffect(() => {
        if (!job || job.status === "completed" || job.status === "failed") return;
        const timer = setInterval(async () => {
            try {
                const next = await api_endpoints.bulkStatus(job.jobId);
                setJob(next);
                if (next.status === "completed") {
                    toast.success(t("bulk.analysisComplete"));
                    window.dispatchEvent(new CustomEvent("credits:refresh"));
                }
            } catch (e) {
                // noop
            }
        }, 1100);
        return () => clearInterval(timer);
    }, [job, t]);

    const urls = React.useMemo(
        () =>
            text
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter(Boolean),
        [text],
    );

    async function start() {
        if (urls.length === 0) {
            toast.error(t("bulk.errorNoUrls"));
            return;
        }
        if (urls.length > 20) {
            toast.error(t("bulk.errorMaxUrls"));
            return;
        }
        setSubmitting(true);
        try {
            const r = await api_endpoints.bulkAnalyse({
                urls,
                collectionId: collectionId || null,
            });
            toast.success(t("bulk.jobStarted"));
            const next = await api_endpoints.bulkStatus(r.jobId);
            setJob(next || r);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSubmitting(false);
        }
    }

    const processed = job?.processedCount || 0;
    const totalU = job?.totalUrls || 0;
    const pct = totalU ? Math.round((processed / totalU) * 100) : 0;

    const effectiveStatus = React.useMemo(() => {
        if (job?.status === "completed" && job?.results?.length > 0 && job.results.every((r) => !r.success)) {
            return "failed";
        }
        return job?.status;
    }, [job]);

    return (
        <div>
            <PageHeader
                eyebrow={t("bulk.eyebrow")}
                title={t("bulk.title")}
            />

            <div className="p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Input */}
                <Card className="lg:col-span-2 p-6" testid="bulk-input-card">
                    <Eyebrow>{t("bulk.urlsLabel")}</Eyebrow>
                    <textarea
                        className="w-full mt-3 border-2 border-black bg-white p-4 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-black min-h-[260px] overflow-x-hidden"
                        placeholder={"https://autoscout24.de/offer/...\nhttps://mobile.de/...\nhttps://otomoto.pl/..."}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        disabled={!!job && job.status !== "completed" && job.status !== "failed"}
                        data-testid="bulk-textarea"
                    />
                    <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <Eyebrow>{t("bulk.collectionLabel")}</Eyebrow>
                            <Select value={collectionId} onValueChange={setCollectionId}>
                                <SelectTrigger data-testid="bulk-collection-select" className="w-auto border-2 border-black bg-white dark:bg-zinc-900 px-3 py-2 font-mono text-xs uppercase tracking-[0.15em]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {collections.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                                {t("bulk.urlCountLabel", { count: urls.length })}
                            </div>
                            <Btn
                                variant="primary"
                                icon={Play}
                                onClick={start}
                                disabled={
                                    submitting ||
                                    !urls.length ||
                                    (job && job.status !== "completed" && job.status !== "failed")
                                }
                                testid="bulk-start"
                            >
                                {t("bulk.startAnalysis")}
                            </Btn>
                        </div>
                    </div>
                </Card>

                {/* Progress / results */}
                <Card className="lg:col-span-1 p-6" testid="bulk-progress-card">
                    <Eyebrow>{t("bulk.statusLabel")}</Eyebrow>
                    {!job ? (
                        <div className="mt-6 text-center py-10">
                            <Files size={28} className="mx-auto text-zinc-400" />
                            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mt-3">
                                {t("bulk.noActiveJob")}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="mt-4 flex items-center gap-2">
                                {effectiveStatus === "completed" ? (
                                    <CheckCircle2 size={16} className="text-[#34C759]" />
                                ) : effectiveStatus === "failed" ? (
                                    <AlertCircle size={16} className="text-[#FF3B30]" />
                                ) : (
                                    <Loader2 size={16} className="animate-spin" />
                                )}
                                <span className="font-mono text-xs uppercase tracking-[0.2em]">
                                    {effectiveStatus}
                                </span>
                            </div>
                            <div className="mt-4 font-display text-3xl leading-none">
                                {processed} / {totalU}
                            </div>
                            <div className="mt-3 h-3 border-2 border-black bg-zinc-200 dark:bg-zinc-700 relative overflow-hidden">
                                <div
                                    className="absolute inset-y-0 left-0 bg-[#FFCC00] transition-all"
                                    style={{ width: pct + "%" }}
                                    data-testid="bulk-progress-bar"
                                />
                            </div>
                            <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2">
                                {t("bulk.jobId", { id: job.jobId?.slice(0, 16) })}
                            </div>
                        </>
                    )}
                </Card>

                {/* Results */}
                {job?.results?.length > 0 && (
                    <Card className="lg:col-span-3 p-0 overflow-hidden">
                        <div className="border-b-2 border-black p-4">
                            <Eyebrow>{t("bulk.resultsLabel")}</Eyebrow>
                            <div className="font-display uppercase text-lg mt-1">
                                {t("bulk.processedCount", { count: job.results.length })}
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="bulk-results">
                                <thead className="border-b-2 border-black bg-zinc-50 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                                    <tr>
                                        <th className="p-3 text-left">{t("bulk.colUrl")}</th>
                                        <th className="p-3 text-left">{t("bulk.colStatus")}</th>
                                        <th className="p-3 text-left">{t("bulk.colCar")}</th>
                                        <th className="p-3 text-left">{t("bulk.colScore")}</th>
                                        <th className="p-3 text-left">{t("bulk.colVerdict")}</th>
                                        <th className="p-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {job.results.map((r, i) => (
                                        <tr
                                            key={i}
                                            data-testid={`bulk-row-${i}`}
                                            className="border-b border-zinc-200 last:border-b-0"
                                        >
                                            <td className="p-3 max-w-[300px]">
                                                <span className="font-mono text-xs truncate block">
                                                    {r.url}
                                                </span>
                                            </td>
                                            <td className="p-3">
                                                {r.success ? (
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#34C759]">
                                                        ✓ ok
                                                    </span>
                                                ) : (
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30]">
                                                        ✗ {r.error}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3 font-display uppercase tracking-tight">
                                                {r.make} {r.model} {r.year}
                                            </td>
                                            <td className="p-3 font-display text-lg">{r.score ?? "—"}</td>
                                            <td className="p-3">
                                                {r.verdict && <VerdictBadge verdict={r.verdict} size="sm" />}
                                            </td>
                                            <td className="p-3 text-right">
                                                {r.listingId && (
                                                    <Link
                                                        to={`/app/analysis/${r.listingId}`}
                                                        className="font-mono text-[10px] uppercase tracking-[0.2em] hover:underline"
                                                    >
                                                        {t("bulk.open")}
                                                    </Link>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}
